import createTypography from '@mui/material/styles/createTypography';
import React from 'react';
import palette from './palette';

function pxToRem(value) {
	return `${value / 16}rem`;
}

function responsiveFontSizes({ sm, md, lg }) {
	return {
		'@media (max-width:600px)': {
			fontSize: pxToRem(sm),
		},
		'@media (min-width:900px)': {
			fontSize: pxToRem(md),
		},
		'@media (min-width:1200px)': {
			fontSize: pxToRem(lg),
		},
	};
}

const FONT_PRIMARY = 'RubikRegular, Public Sans, sans-serif';

/*type typo = {
    lineHeight: number;
    fontSize: any;
    fontWeight?:number;
}*/

declare module '@mui/material/styles/createTypography' {
	interface CustomTypography {
		body3: React.CSSProperties;
		body4: React.CSSProperties;
		body5: React.CSSProperties;
		boldBody1: React.CSSProperties;
		boldBody2: React.CSSProperties;
		boldBody17: React.CSSProperties;
		boldBody18: React.CSSProperties;
		h3_5: React.CSSProperties;
		semiCaption: React.CSSProperties;
		captionRegular: React.CSSProperties;
		semiCaptionRegular: React.CSSProperties;
		boldMedium: React.CSSProperties;
		lightSmall: React.CSSProperties;
		boldLarge: React.CSSProperties;
		boldSmall: React.CSSProperties;
		alertBody: React.CSSProperties;
		h4_bold: React.CSSProperties;
		h4_light: React.CSSProperties;
	}

	interface Typography extends CustomTypography {}
	interface TypographyOptions extends CustomTypography {}
}

export const typography = createTypography(palette, {
	fontFamily: FONT_PRIMARY,
	fontWeightRegular: 400,
	fontWeightMedium: 600,
	fontWeightBold: 700,
	h1: {
		fontWeight: 700,
		lineHeight: 80 / 64,
		fontSize: pxToRem(40),
		...responsiveFontSizes({ sm: 52, md: 58, lg: 64 }),
	},
	h2: {
		fontWeight: 700,
		lineHeight: 64 / 48,
		fontSize: pxToRem(32),
		...responsiveFontSizes({ sm: 30, md: 32, lg: 32 }),
	},
	h3: {
		lineHeight: 1.5,
		fontSize: pxToRem(24),
		...responsiveFontSizes({ sm: 20, md: 24, lg: 24 }),
	},
	h3_5: {
		lineHeight: 1,
		fontSize: pxToRem(22),
		...responsiveFontSizes({ sm: 21, md: 22, lg: 22 }),
	},
	h4: {
		lineHeight: 1.5,
		fontSize: pxToRem(20),
		...responsiveFontSizes({ sm: 16, md: 18, lg: 20 }),
	},
	h4_bold: {
		fontFamily: 'PloniBold',
		lineHeight: pxToRem(24),
		fontSize: pxToRem(20),
		...responsiveFontSizes({ sm: 16, md: 18, lg: 20 }),
	},
	h4_light: {
		fontSize: pxToRem(20),
		fontFamily: 'RubikLight',
		...responsiveFontSizes({ sm: 16, md: 18, lg: 20 }),
	},
	h5: {
		fontFamily: 'RubikMedium',
		lineHeight: 1.5,
		fontSize: pxToRem(18),
		...responsiveFontSizes({ sm: 14, md: 16, lg: 18 }),
	},
	h6: {
		lineHeight: 28 / 18,
		fontSize: pxToRem(17),
		fontFamily: 'RubikLight',
		...responsiveFontSizes({ sm: 12, md: 17, lg: 17 }),
	},
	subtitle1: {
		lineHeight: 1.5,
		fontSize: pxToRem(16),
	},
	subtitle2: {
		lineHeight: 22 / 14,
		fontSize: pxToRem(14),
	},
	body1: {
		lineHeight: 1.5,
		fontSize: pxToRem(14),
		...responsiveFontSizes({ sm: 13, md: 13, lg: 14 }),
	},
	body2: {
		lineHeight: 22 / 14,
		fontSize: pxToRem(14),
	},
	body3: {
		lineHeight: 22 / 15,
		fontSize: pxToRem(15),
		fontFamily: 'RubikLight',
	},
	body4: {
		lineHeight: 1,
		fontSize: pxToRem(15),
		fontFamily: 'RubikRegular',
	},
	body5: {
		lineHeight: 22 / 15,
		fontSize: pxToRem(13),
		fontFamily: 'RubikRegular',
	},
	boldBody1: {
		lineHeight: 1.5,
		fontSize: pxToRem(14),
		fontFamily: 'RubikMedium',
	},
	boldBody17: {
		lineHeight: 22 / 17,
		fontSize: pxToRem(17),
		...responsiveFontSizes({ sm: 16, md: 17, lg: 17 }),
		fontFamily: 'RubikMedium',
	},
	boldBody18: {
		letterSpacing: '0.005em',
		lineHeight: 1.5,
		fontSize: pxToRem(15),
		...responsiveFontSizes({ sm: 15, md: 15, lg: 15 }),
		fontFamily: 'RubikMedium',
	},
	boldBody2: {
		lineHeight: 22 / 14,
		fontSize: pxToRem(14),
		fontFamily: 'PloniBold',
	},
	alertBody: {
		lineHeight: 22 / 14,
		fontSize: pxToRem(14),
		fontFamily: 'RubikRegular',
	},
	caption: {
		lineHeight: 1.5,
		fontSize: pxToRem(12),
		fontFamily: 'RubikMedium',
	},
	captionRegular: {
		lineHeight: 1.5,
		fontSize: pxToRem(12),
    ...responsiveFontSizes({ sm: 11, md: 12, lg: 12 }),
	},
	semiCaption: {
		lineHeight: 1.5,
		fontSize: pxToRem(11),
		fontFamily: 'RubikSemiBold',
	},
	semiCaptionRegular: {
		lineHeight: 1,
		fontSize: pxToRem(11),
	},
	overline: {
		fontWeight: 700,
		lineHeight: 1.5,
		fontSize: pxToRem(12),
		letterSpacing: 1.1,
		textTransform: 'uppercase',
	},
	button: {
		fontWeight: 700,
		lineHeight: 24 / 14,
		fontSize: pxToRem(14),
		textTransform: 'capitalize',
	},
	boldMedium: {
		fontSize: pxToRem(15),
		fontFamily: 'RubikMedium',
		fontWeight: 500,
	},
	boldSmall: {
		fontSize: pxToRem(12),
		fontFamily: 'RubikMedium',
		fontWeight: 500,
	},
	lightSmall: {
		fontSize: pxToRem(12),
		fontWeight: 'normal',
	},
	boldLarge: {
		fontSize: pxToRem(18),
		lineHeight: 'normal',
		marginRight: '20px',
	},
});
